import React, { useState, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Persona } from '../../types/persona';
import { TeamCreate } from '../../types/team';
import { PersonaCard } from '../personas/PersonaCard';
import { usePersonas } from '../../hooks/usePersonas';
import * as rosterService from '../../services/rosterService';

// Drag item types
const ItemTypes = {
  PERSONA: 'persona'
};

// Draggable persona component
const DraggablePersona = ({ persona }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.PERSONA,
    item: { persona },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging()
    })
  }));

  return (
    <div 
      ref={drag} 
      style={{ opacity: isDragging ? 0.5 : 1 }}
      className="cursor-move"
    >
      <PersonaCard persona={persona} />
    </div>
  );
};

// Team member slot component
const TeamMemberSlot = ({ index, member, onDrop, onRemove, onRoleChange }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.PERSONA,
    drop: (item) => onDrop(item.persona, index),
    collect: (monitor) => ({
      isOver: !!monitor.isOver()
    })
  }));

  return (
    <div 
      ref={drop} 
      className={`border-2 rounded-lg p-4 ${isOver ? 'border-blue-500 bg-blue-50' : member ? 'border-gray-200' : 'border-dashed border-gray-300'}`}
      style={{ minHeight: '200px' }}
    >
      {member ? (
        <div>
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-medium">Team Member #{index + 1}</h3>
            <button 
              onClick={() => onRemove(index)}
              className="text-red-500 hover:text-red-700"
            >
              Remove
            </button>
          </div>
          
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={member.role}
              onChange={(e) => onRoleChange(index, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">Select a role</option>
              <option value="Lead">Lead</option>
              <option value="Specialist">Specialist</option>
              <option value="Support">Support</option>
              <option value="Analyst">Analyst</option>
              <option value="Coordinator">Coordinator</option>
            </select>
          </div>
          
          <PersonaCard persona={member.persona} />
        </div>
      ) : (
        <div className="flex items-center justify-center h-full text-gray-400">
          Drop a persona here
        </div>
      )}
    </div>
  );
};

interface TeamBuilderProps {
  initialTeam?: TeamCreate;
  onSave?: (team: TeamCreate) => void;
}

export const TeamBuilder: React.FC<TeamBuilderProps> = ({ 
  initialTeam,
  onSave 
}) => {
  const [teamMembers, setTeamMembers] = useState<Array<{ persona: Persona, role: string }>>([]);
  const [teamName, setTeamName] = useState(initialTeam?.name || '');
  const [teamDescription, setTeamDescription] = useState(initialTeam?.description || '');
  const [teamGoal, setTeamGoal] = useState(initialTeam?.goal || '');
  const [teamTags, setTeamTags] = useState<string[]>(initialTeam?.tags || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [newTag, setNewTag] = useState('');
  const [rosters, setRosters] = useState([]);
  const [selectedRosterId, setSelectedRosterId] = useState(initialTeam?.roster_id || '');
  const [leaderPersonaId, setLeaderPersonaId] = useState(initialTeam?.leader_persona || '');

  // Personas via the shared hook, gated on roster selection (preserves the old fetch timing)
  const { data: personas = [] } = usePersonas({ enabled: !!selectedRosterId });

  // Load rosters
  useEffect(() => {
    const loadRosters = async () => {
      try {
        const data = await rosterService.getRosters();
        setRosters(data);
        
        // If no roster is selected and we have rosters, select the first one
        if (!selectedRosterId && data.length > 0) {
          setSelectedRosterId(data[0].id);
        }
      } catch (error) {
        console.error('Failed to load rosters:', error);
      }
    };
    
    loadRosters();
  }, []);
  
  // Initialize team members if initialTeam is provided
  useEffect(() => {
    if (initialTeam && selectedRosterId) {
      // In a real implementation, you would load team members from the initialTeam
      // For now, we'll just set an empty array
      setTeamMembers([]);
    }
  }, [initialTeam, selectedRosterId]);
  
  // Filter personas based on search query
  const filteredPersonas = searchQuery
    ? personas.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : personas;
  
  // Handle dropping a persona into a team slot
  const handleDrop = (persona: Persona, index: number) => {
    const newMembers = [...teamMembers];
    
    // Check if this persona is already in the team
    const existingIndex = teamMembers.findIndex(m => m.persona.id === persona.id);
    
    if (existingIndex >= 0 && existingIndex !== index) {
      // If the persona is already in another slot, remove it from there
      newMembers.splice(existingIndex, 1);
    }
    
    // Add or update the persona in the specified slot
    if (index < newMembers.length) {
      newMembers[index] = { persona, role: newMembers[index]?.role || '' };
    } else {
      // Fill any gaps with empty slots
      while (newMembers.length < index) {
        newMembers.push(null);
      }
      newMembers.push({ persona, role: '' });
    }
    
    setTeamMembers(newMembers);
  };
  
  // Handle removing a team member
  const handleRemoveMember = (index: number) => {
    const newMembers = [...teamMembers];
    newMembers.splice(index, 1);
    setTeamMembers(newMembers);
  };
  
  // Handle changing a team member's role
  const handleRoleChange = (index: number, role: string) => {
    const newMembers = [...teamMembers];
    newMembers[index] = { ...newMembers[index], role };
    setTeamMembers(newMembers);
  };
  
  // Add a tag to the team
  const handleAddTag = () => {
    if (newTag && !teamTags.includes(newTag)) {
      setTeamTags([...teamTags, newTag]);
      setNewTag('');
    }
  };
  
  // Remove a tag from the team
  const handleRemoveTag = (tag: string) => {
    setTeamTags(teamTags.filter(t => t !== tag));
  };
  
  // Save the team
  const handleSaveTeam = () => {
    if (!teamName || !teamGoal || !selectedRosterId) {
      alert('Please provide a team name, goal, and select a roster');
      return;
    }
    
    const team: TeamCreate = {
      name: teamName,
      description: teamDescription,
      roster_id: selectedRosterId,
      leader_persona: leaderPersonaId || undefined,
      goal: teamGoal,
      tags: teamTags,
      workflow: []
    };
    
    if (onSave) {
      onSave(team);
    }
  };
  
  return (
    <DndProvider backend={HTML5Backend}>
      <div className="team-builder">
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-4">Team Builder</h2>
          <p className="text-gray-600">
            Drag and drop personas to build your team. Assign roles to each team member.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Persona selection panel */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <h3 className="text-lg font-medium mb-4">Available Personas</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Roster
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={selectedRosterId}
                onChange={(e) => setSelectedRosterId(e.target.value)}
              >
                <option value="">Select a roster</option>
                {rosters.map(roster => (
                  <option key={roster.id} value={roster.id}>
                    {roster.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search personas..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {filteredPersonas.map(persona => (
                <DraggablePersona key={persona.id} persona={persona} />
              ))}
              
              {filteredPersonas.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No personas found
                </div>
              )}
            </div>
          </div>
          
          {/* Team composition area */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <h3 className="text-lg font-medium mb-4">Team Composition</h3>
            
            <div className="space-y-4">
              {[...Array(Math.max(teamMembers.length + 1, 3))].map((_, index) => (
                <TeamMemberSlot
                  key={index}
                  index={index}
                  member={teamMembers[index]}
                  onDrop={handleDrop}
                  onRemove={handleRemoveMember}
                  onRoleChange={handleRoleChange}
                />
              ))}
            </div>
          </div>
          
          {/* Team configuration panel */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <h3 className="text-lg font-medium mb-4">Team Configuration</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Team Name *
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Enter team name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={3}
                  value={teamDescription}
                  onChange={(e) => setTeamDescription(e.target.value)}
                  placeholder="Enter team description"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Goal *
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={3}
                  value={teamGoal}
                  onChange={(e) => setTeamGoal(e.target.value)}
                  placeholder="What is this team designed to accomplish?"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leader Persona
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={leaderPersonaId}
                  onChange={(e) => setLeaderPersonaId(e.target.value)}
                >
                  <option value="">Select a leader</option>
                  {teamMembers.map((member, index) => (
                    <option key={index} value={member.persona.id}>
                      {member.persona.name} ({member.role})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <div className="flex">
                  <input
                    type="text"
                    className="flex-grow px-3 py-2 border border-gray-300 rounded-l-md"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add a tag"
                  />
                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700"
                    onClick={handleAddTag}
                  >
                    Add
                  </button>
                </div>
                
                <div className="flex flex-wrap gap-2 mt-2">
                  {teamTags.map(tag => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full flex items-center"
                    >
                      {tag}
                      <button
                        type="button"
                        className="ml-2 text-gray-500 hover:text-gray-700"
                        onClick={() => handleRemoveTag(tag)}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                  {teamTags.length === 0 && (
                    <p className="text-sm text-gray-500">No tags added yet</p>
                  )}
                </div>
              </div>
              
              <div className="pt-4">
                <button
                  className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  onClick={handleSaveTeam}
                >
                  Save Team
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DndProvider>
  );
};
